import { useState, useEffect, useCallback } from "react";
import { useQuery, useQueryClient, useMutation } from "react-query";
import {
  findMissingKeys,
  findEmptyValues,
  addMissingKeys,
  copyFromMaster,
  searchTranslation,
  removeKeys,
  findNonMasterKeys,
} from "../utils/translationUtils";
import {
  downloadTranslationFile,
  uploadTranslationFile,
  getTranslationFiles,
  aiTranslate,
  TranslationItem,
} from "../utils/translationS3Api";
import { TranslationObject, TranslationIssues } from "../types";
import { useClient } from "utils/client";

interface UseTranslationsReturn {
  languages: string[];
  translations: Record<string, TranslationObject>;
  masterTranslation: TranslationObject;
  masterLang: string;
  selectedLang: string;
  setSelectedLang: (lang: string) => void;
  isLoading: boolean;
  error: string | null;
  getLanguageIssues: (lang: string) => TranslationIssues;
  addMissingKeysToLang: (
    lang: string
  ) => Promise<TranslationObject | undefined>;
  copyKeyFromMaster: (
    lang: string,
    key: string
  ) => Promise<TranslationObject | undefined>;
  updateTranslation: (
    lang: string,
    key: string,
    value: string | TranslationObject
  ) => Promise<TranslationObject | undefined>;
  translateWithAI: (
    lang: string,
    key: string
  ) => Promise<TranslationObject | undefined>;
  translateMultipleWithAI: (
    lang: string,
    keys: string[]
  ) => Promise<TranslationObject | undefined>;
  deleteTranslationKeys: (
    lang: string,
    keys: string[]
  ) => Promise<TranslationObject | undefined>;
  deleteNonMasterKeys: (lang: string) => Promise<TranslationObject | undefined>;
  searchInTranslation: (lang: string, searchTerm: string) => string[];
}

export const useTranslations = (masterLang = "de"): UseTranslationsReturn => {
  const queryClient = useQueryClient();
  const client = useClient();
  const [selectedLang, setSelectedLang] = useState<string>("");

  // Fetch languages
  const {
    data: languages = [],
    isLoading: isLoadingLanguages,
    error: languagesError,
  } = useQuery({
    queryKey: ["languages"],
    queryFn: () => getTranslationFiles(client),
    onSuccess: (langs) => {
      if (langs.length > 0 && !selectedLang) {
        setSelectedLang(langs.includes(masterLang) ? masterLang : langs[0]);
      }
    },
  });

  // Fetch master translation
  const {
    data: masterTranslation = {} as TranslationObject,
    isLoading: isLoadingMaster,
    error: masterError,
  } = useQuery({
    queryKey: ["translation", masterLang],
    queryFn: () =>
      downloadTranslationFile(client, `translation.${masterLang}.json`),
    enabled: !!masterLang,
  });

  // Fetch all translations
  const {
    data: translationsMap = {} as Record<string, TranslationObject>,
    isLoading: isLoadingTranslations,
    error: translationsError,
  } = useQuery({
    queryKey: ["translations", languages],
    queryFn: async () => {
      const translationsData: Record<string, TranslationObject> = {};

      // Fetch translations in parallel
      await Promise.all(
        languages
          .filter((lang) => lang !== masterLang)
          .map(async (lang) => {
            try {
              // Check if we already have a cached query
              const existingData = queryClient.getQueryData<TranslationObject>([
                "translation",
                lang,
              ]);

              if (existingData) {
                translationsData[lang] = existingData;
              } else {
                const translation = await downloadTranslationFile(
                  client,
                  `translation.${lang}.json`
                );
                translationsData[lang] = translation;
                // Cache individual translation
                queryClient.setQueryData(["translation", lang], translation);
              }
            } catch (error) {
              console.error(`Error loading translation for ${lang}`, error);
            }
          })
      );

      return translationsData;
    },
    enabled: languages.length > 0,
  });

  // Mutation for saving translations
  const updateTranslationMutation = useMutation({
    mutationFn: async ({
      lang,
      data,
    }: {
      lang: string;
      data: TranslationObject;
    }) => {
      await uploadTranslationFile(client, `translation.${lang}.json`, data);
      return { success: true };
    },
    onSuccess: (_, variables) => {
      const { lang, data } = variables;
      // Update cache
      queryClient.setQueryData(["translation", lang], data);
      // Update translations map
      queryClient.setQueryData(
        ["translations", languages],
        (old: Record<string, TranslationObject> | undefined) => ({
          ...(old || {}),
          [lang]: data,
        })
      );
    },
  });

  // Get issues for a specific language
  const getLanguageIssues = useCallback(
    (lang: string): TranslationIssues => {
      if (!translationsMap[lang] || !masterTranslation) {
        return {
          missingKeys: [],
          emptyValues: [],
          nonMasterKeys: [],
        };
      }

      const missingKeys = findMissingKeys(
        masterTranslation,
        translationsMap[lang]
      );
      const emptyValues = findEmptyValues(translationsMap[lang]);
      const nonMasterKeys = findNonMasterKeys(
        masterTranslation,
        translationsMap[lang]
      );

      return { missingKeys, emptyValues, nonMasterKeys };
    },
    [translationsMap, masterTranslation]
  );

  // Add missing keys from master to a translation
  const addMissingKeysToLang = useCallback(
    async (lang: string): Promise<TranslationObject | undefined> => {
      if (!translationsMap[lang] || !masterTranslation) return;

      const updated = addMissingKeys(masterTranslation, translationsMap[lang]);

      // Make sure to await the mutation to ensure completion before returning
      await updateTranslationMutation.mutateAsync({ lang, data: updated });

      return updated;
    },
    [translationsMap, masterTranslation, updateTranslationMutation]
  );

  // Copy a key value from master to a translation
  const copyKeyFromMaster = useCallback(
    async (
      lang: string,
      key: string
    ): Promise<TranslationObject | undefined> => {
      if (!translationsMap[lang] || !masterTranslation) return;

      const updated = copyFromMaster(
        masterTranslation,
        translationsMap[lang],
        key
      );

      // Make sure to await the mutation to ensure completion before returning
      await updateTranslationMutation.mutateAsync({ lang, data: updated });

      return updated;
    },
    [translationsMap, masterTranslation, updateTranslationMutation]
  );

  // Update a translation value
  const updateTranslation = useCallback(
    async (
      lang: string,
      key: string,
      value: string | TranslationObject
    ): Promise<TranslationObject | undefined> => {
      if (!translationsMap[lang]) return;

      // If no key is provided and value is an object, replace the entire translation
      if (key === "" && typeof value === "object") {
        updateTranslationMutation.mutate({ lang, data: value });
        return value;
      }

      // Otherwise, update a specific key
      // Create a deep copy of the translation
      const translationCopy = JSON.parse(JSON.stringify(translationsMap[lang]));

      // Update the value using the nested keys
      const keys = key.split(".");
      let current: any = translationCopy;

      for (let i = 0; i < keys.length - 1; i++) {
        current = current[keys[i]];
      }

      current[keys[keys.length - 1]] = value;

      updateTranslationMutation.mutate({ lang, data: translationCopy });

      return translationCopy;
    },
    [translationsMap, updateTranslationMutation]
  );

  // Use AI to translate a single value
  const translateWithAI = useCallback(
    async (
      lang: string,
      key: string
    ): Promise<TranslationObject | undefined> => {
      if (!translationsMap[lang] || !masterTranslation) return;

      // Get the master value for this key
      const keys = key.split(".");
      let current: any = masterTranslation;

      for (let i = 0; i < keys.length - 1; i++) {
        current = current[keys[i]];
      }

      const masterValue = current[keys[keys.length - 1]];

      if (!masterValue) return;

      // Translate using AI - now always returns an array of TranslationItems
      const translatedItems = await aiTranslate(
        client,
        masterValue,
        masterLang,
        lang
      );

      // Get the first (and only) translation result
      const translatedValue = translatedItems[0]?.text || "";

      // Update the translation
      return updateTranslation(lang, key, translatedValue);
    },
    [masterLang, translationsMap, masterTranslation, updateTranslation, client]
  );

  // Use AI to translate multiple values at once
  const translateMultipleWithAI = useCallback(
    async (
      lang: string,
      keyArray: string[]
    ): Promise<TranslationObject | undefined> => {
      if (!translationsMap[lang] || !masterTranslation || keyArray.length === 0)
        return;

      // Create an array to hold the translation items with keys and text
      const translationItems: TranslationItem[] = [];
      const validKeys: string[] = [];

      // Collect all valid keys and their corresponding master values
      for (const key of keyArray) {
        const keyParts = key.split(".");
        let current: any = masterTranslation;

        try {
          for (let i = 0; i < keyParts.length - 1; i++) {
            current = current[keyParts[i]];
          }

          const masterValue = current[keyParts[keyParts.length - 1]];

          if (masterValue && typeof masterValue === "string") {
            // Create a translation item with both key and text
            translationItems.push({
              key: key,
              text: masterValue,
            });
            validKeys.push(key);
          }
        } catch (error) {
          console.error(`Error accessing key ${key}:`, error);
          // Skip invalid keys
        }
      }

      if (validKeys.length === 0) return;

      // If only one key, use the single translation function for simplicity
      if (validKeys.length === 1) {
        return translateWithAI(lang, validKeys[0]);
      }

      // Translate all values at once using AI with the key-value pairs
      const translatedItems = (await aiTranslate(
        client,
        translationItems,
        masterLang,
        lang
      )) as TranslationItem[];

      // Update the translation object with translated values
      let updatedTranslation = JSON.parse(
        JSON.stringify(translationsMap[lang])
      );

      // Process each translated item
      for (const item of translatedItems) {
        if (item.key && item.text) {
          // Apply the update in our local copy
          const keyParts = item.key.split(".");
          let current: any = updatedTranslation;

          for (let j = 0; j < keyParts.length - 1; j++) {
            // Ensure the path exists
            if (!current[keyParts[j]]) {
              current[keyParts[j]] = {};
            }
            current = current[keyParts[j]];
          }

          // Set the translated text
          current[keyParts[keyParts.length - 1]] = item.text;
        }
      }

      // Save the updated translation
      await updateTranslationMutation.mutateAsync({
        lang,
        data: updatedTranslation,
      });

      return updatedTranslation;
    },
    [
      masterLang,
      translationsMap,
      masterTranslation,
      translateWithAI,
      updateTranslationMutation,
      client,
    ]
  );

  // Delete keys from a translation
  const deleteTranslationKeys = useCallback(
    async (
      lang: string,
      keys: string[]
    ): Promise<TranslationObject | undefined> => {
      if (!translationsMap[lang]) return;

      // Create an updated translation with the keys removed
      const updatedTranslation = removeKeys(translationsMap[lang], keys);

      // Save the updated translation and await completion
      await updateTranslationMutation.mutateAsync({
        lang,
        data: updatedTranslation,
      });

      return updatedTranslation;
    },
    [translationsMap, updateTranslationMutation]
  );

  // Delete all keys that don't exist in the master file
  const deleteNonMasterKeys = useCallback(
    async (lang: string): Promise<TranslationObject | undefined> => {
      if (!translationsMap[lang] || !masterTranslation) return;

      // Find keys that exist in translation but not in master
      const nonMasterKeys = findNonMasterKeys(
        masterTranslation,
        translationsMap[lang]
      );

      if (nonMasterKeys.length === 0) {
        return translationsMap[lang]; // No changes needed
      }

      // Remove non-master keys
      return deleteTranslationKeys(lang, nonMasterKeys);
    },
    [translationsMap, masterTranslation, deleteTranslationKeys]
  );

  // Search in translations
  const searchInTranslation = useCallback(
    (lang: string, searchTerm: string): string[] => {
      if (!translationsMap[lang]) return [];

      return searchTranslation(translationsMap[lang], searchTerm);
    },
    [translationsMap]
  );

  // To individually fetch a specific translation if needed
  const fetchTranslation = useCallback(
    async (lang: string): Promise<TranslationObject | null> => {
      if (!lang) return null;

      const translation = await downloadTranslationFile(
        client,
        `translation.${lang}.json`
      );
      queryClient.setQueryData(["translation", lang], translation);

      // Also update the translations map
      queryClient.setQueryData(
        ["translations", languages],
        (old: Record<string, TranslationObject> | undefined) => ({
          ...(old || {}),
          [lang]: translation,
        })
      );

      return translation;
    },
    [queryClient, languages, client]
  );

  // Pre-fetch selected language translation if changing languages
  useEffect(() => {
    if (selectedLang && !translationsMap[selectedLang]) {
      fetchTranslation(selectedLang);
    }
  }, [selectedLang, translationsMap, fetchTranslation]);

  // Compute overall loading state
  const isLoading =
    isLoadingLanguages || isLoadingMaster || isLoadingTranslations;

  // Compute overall error
  const error = languagesError || masterError || translationsError;

  return {
    languages,
    translations: translationsMap,
    masterTranslation,
    masterLang,
    selectedLang,
    setSelectedLang,
    isLoading,
    error: error ? (error as Error).message : null,
    getLanguageIssues,
    addMissingKeysToLang,
    copyKeyFromMaster,
    updateTranslation,
    translateWithAI,
    translateMultipleWithAI,
    deleteTranslationKeys,
    deleteNonMasterKeys,
    searchInTranslation,
  };
};
